import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/project/karuna/components/ui/card";
import { Button } from "@/project/karuna/components/ui/button"; // @ts-ignore - button is not a valid module
import { Mail, Phone, MapPin, Clock } from "lucide-react";
import { useMemo, useState } from "react";
import PublicLayout from "./PublicLayout";
import { createForm } from "@formily/core";
import SchemaFormRender from "@/components/Schema/SchemaFormRender";
import axios from "axios";
import { notify } from "@/components/common/Notification";

const CONTACT_SCHEMA = {
  form: {
    labelCol: 6,
    wrapperCol: 12,
    layout: "vertical",
  },
  schema: {
    type: "object",
    properties: {
      layoutContainer: {
        type: "void",
        "x-component": "FormLayout",
        "x-component-props": { maxColumns: 1, minColumns: 1, columnGap: 20 },
        name: "layoutContainer",
        "x-designable-id": "contact_layout_001",
        "x-index": 0,
        properties: {
          contactInfo: {
            type: "void",
            "x-component": "Card",
            "x-index": 0,
            "x-component-props": { title: "Contact Form" },
            name: "contactInfo",
            "x-designable-id": "contact_card_001",
            properties: {
              contactGrid: {
                type: "void",
                "x-component": "FormGrid",
                "x-component-props": {
                  maxColumns: 2,
                  minColumns: 2,
                  columnGap: 20,
                  "is-in-grid": true,
                },
                name: "contactGrid",
                "x-designable-id": "contact_grid_001",
                "x-index": 0,
                properties: {
                  fullname: {
                    type: "string",
                    title: "Full Name",
                    "x-decorator": "FormItem",
                    "x-component": "Input",
                    "x-component-props": {
                      gridSpan: 1,
                      placeholder: "E.g. Ravi Bansal",
                      "is-in-grid": true,
                    },
                    "x-decorator-props": { layout: "vertical" },
                    name: "fullname",
                    "x-designable-id": "contact_fullname_001",
                    "x-index": 0,
                    "x-validator": [],
                  },
                  email: {
                    type: "string",
                    title: "Email",
                    "x-decorator": "FormItem",
                    "x-component": "Input",
                    "x-component-props": {
                      gridSpan: 1,
                      placeholder: "john@example.com",
                      "is-in-grid": true,
                    },
                    "x-decorator-props": { layout: "vertical" },
                    name: "email",
                    "x-designable-id": "contact_email_001",
                    "x-index": 1,
                    "x-validator": [
                      { required: true, message: "Email is required" },
                      "email",
                    ],
                  },
                  phone: {
                    type: "string",
                    title: "Phone Number",
                    "x-decorator": "FormItem",
                    "x-component": "Input",
                    "x-component-props": {
                      gridSpan: 1,
                      "is-in-grid": true,
                    },
                    "x-decorator-props": { layout: "vertical" },
                    name: "phone",
                    "x-designable-id": "contact_phone_001",
                    "x-index": 2,
                    "x-validator": [
                      {
                        pattern: "^[0-9]{10}$",
                        message: "Phone number must be exactly 10 digits",
                      },
                    ],
                  },
                  interest: {
                    type: "string",
                    title: "Interest",
                    "x-decorator": "FormItem",
                    "x-component": "Select",
                    "x-component-props": {
                      gridSpan: 1,
                      placeholder: "Select your interest",
                      "is-in-grid": true,
                    },
                    "x-decorator-props": { layout: "vertical" },
                    name: "interest",
                    "x-designable-id": "contact_interest_001",
                    "x-index": 3,
                    enum: [
                      {
                        label: "As a Resource Person",
                        value: "resource-person",
                      },
                      {
                        label: "To join as a member of Karuna International",
                        value: "member",
                      },
                      { label: "As a part-time volunteer", value: "volunteer" },
                    ],
                    "x-validator": [],
                  },
                  message: {
                    type: "string",
                    title: "Message",
                    "x-decorator": "FormItem",
                    "x-component": "TextArea",
                    "x-component-props": {
                      gridSpan: 2,
                      placeholder: "Tell us how you can help...",
                      rows: 5,
                      "is-in-grid": true,
                    },
                    "x-decorator-props": { layout: "vertical" },
                    name: "message",
                    "x-designable-id": "contact_message_001",
                    "x-index": 4,
                    "x-validator": [
                      { required: true, message: "Message is required" },
                    ],
                  },
                },
              },
            },
          },
        },
      },
    },
    "x-designable-id": "kszuno8ogod",
  },
};

const SCHEMA_NAME = "Karuna Contact Us";

const Contact = () => {
  const form = useMemo(() => createForm({}), []);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    await form.validate();
    setSubmitting(true);
    try {
      await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/api/formily/dynamic-model/${SCHEMA_NAME}/`,
        form.values,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Tenant-Id": import.meta.env.VITE_X_TENANTID,
          },
        }
      );
      form.reset();
      notify.success(
        "Message Sent!",
        "We'll get back to you within 24-48 hours."
      );
    } catch (_error) {
      notify.error(
        "Submission Failed",
        "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicLayout>
      {/* Hero Section */}
      <section className="w-full bg-gradient-hero py-20 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="mb-6 text-5xl font-bold">Contact Us</h1>
          <p className="mx-auto max-w-3xl text-xl">
            We&apos;d love to hear from you. Reach out to learn more about our
            programs or get involved.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="relative z-10 mx-auto -mt-16 grid max-w-6xl gap-6 md:grid-cols-4">
            <Card className="shadow-strong">
              <CardHeader className="text-center">
                <Mail className="mx-auto mb-2 h-10 w-10 text-primary" />
                <CardTitle>Email</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <a
                  href="mailto:info@karunainternational.org"
                  className="text-sm text-primary hover:underline"
                >
                  info@karunainternational.org
                </a>
              </CardContent>
            </Card>

            <Card className="shadow-strong">
              <CardHeader className="text-center">
                <Phone className="mx-auto mb-2 h-10 w-10 text-secondary" />
                <CardTitle>Phone</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <a
                  href="tel:+917449493838"
                  className="text-sm text-primary hover:underline"
                >
                  +91 74 4949 3838
                </a>
              </CardContent>
            </Card>

            <Card className="shadow-strong">
              <CardHeader className="text-center">
                <MapPin className="mx-auto mb-2 h-10 w-10 text-accent" />
                <CardTitle>Address</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-sm text-muted-foreground">Chennai, India.</p>
              </CardContent>
            </Card>

            <Card className="shadow-strong">
              <CardHeader className="text-center">
                <Clock className="mx-auto mb-2 h-10 w-10 text-primary" />
                <CardTitle>Hours</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-sm text-muted-foreground">
                  Mon-Fri: 9AM-6PM
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="bg-muted/30 py-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-4xl">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold">Send Us a Message</h2>
              <div className="mx-auto mb-6 h-1 w-24 bg-primary" />
              <p className="text-xl text-muted-foreground">
                Fill out the form below and we&apos;ll get back to you as soon
                as possible
              </p>
            </div>
            <div className="rounded-lg border bg-white p-6 shadow-strong">
              <SchemaFormRender form={form} schemaJson={CONTACT_SCHEMA} />
              <div className="mt-6">
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="w-full md:w-auto"
                >
                  {submitting ? "Sending..." : "Send Message"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default Contact;
